import type { LatestReading } from '../api/types'
import { formatDateTime, relativeAge } from '../lib/time'
import { type Freshness, verdict } from '../lib/freshness'
import { displayValue } from '../lib/format'
import { isSuspect, names } from '../lib/quality'

/** How often the panel re-asks.
 *
 *  Chosen to keep the age on screen honest rather than because the archive
 *  changes this fast — the live collection pass writes at most every
 *  LIVE_INTERVAL_S (300 s by default), and the sensors themselves report on
 *  their own cadence, hourly on the gateways since 2026-08-18. Re-rendering
 *  every half minute is what stops "2 min ago" sitting there saying 2 for an
 *  hour.
 */
export const LATEST_REFRESH_MS = 30_000

/** Stable order: by axis group, then display name.
 *
 *  The endpoint returns rows in whatever order the index probe found them,
 *  and a panel that refreshes on a timer must not reorder itself under the
 *  reader. A value moving to another row while someone is reading it is
 *  worse than one that is slightly stale. Returns a new array — sorting the
 *  caller's in place would reorder React's own view of it.
 */
export function orderReadings(readings: LatestReading[]): LatestReading[] {
  return [...readings].sort((a, b) =>
    a.axis_group.localeCompare(b.axis_group) ||
    a.display_name.localeCompare(b.display_name))
}

/** Whether the newest reading is as recent as this station's cadence implies.
 *
 *  Words only. It predates the status colours (DESIGN_GUIDELINE §2.6,
 *  approved 2026-10-06 and given to station health in intake v2 phase 5),
 *  and follows the convention styles.css records: every state is carried
 *  by its own words, the way the station cards say "3 days behind".
 *
 *  Two intervals of grace, because a station reporting hourly is *expected*
 *  to look up to an hour old most of the time. Flagging that would mean
 *  crying wolf on every gateway node between readings, and a panel that is
 *  usually wrong is one nobody reads.
 */
export function stalenessLabel(ts: string | null, pollIntervalS: number,
                               now: Date = new Date(), silentAfterS?: number): string {
  if (!ts) return 'no readings'
  const at = new Date(ts).getTime()
  if (Number.isNaN(at)) return 'no readings'
  // The monitor's own silent threshold where the station summary gives it,
  // so this line never disagrees with the freshness colours beside it.
  const grace = silentAfterS ? silentAfterS * 1000 : Math.max(pollIntervalS, 60) * 2 * 1000
  return now.getTime() - at <= grace ? 'reporting' : 'no new readings'
}

/** Which single message the panel is entitled to print, if any.
 *
 *  The two conditions are not mutually exclusive and saying both is saying two
 *  false things at once. On a first fetch failure there is an error and no
 *  data, and the obvious pair of guards then claims to be "showing the last
 *  readings received" when none were ever received, *and* that the archive
 *  holds nothing for this station when the only evidence is one failed
 *  request. That is not a corner: every data endpoint answers 401 until a
 *  reader signs in, so it is the path a signed-out tab takes.
 *
 *  So the decision is made once, here, where it can be tested without a DOM.
 *  `'empty'` is a claim about the archive and is only ever reached when the
 *  vendor of that claim — a successful response — exists.
 */
export type Notice = 'stale' | 'failed' | 'empty' | null

export function noticeFor(count: number, error: string | undefined,
                          loading: boolean): Notice {
  if (error) return count ? 'stale' : 'failed'
  if (loading || count) return null
  return 'empty'
}

/** What the page's poll of `/latest` holds (usePolled). Explore polls it
 *  once and shares it with the sidebar's parameter list. */
export interface LatestState {
  data: LatestReading[] | undefined
  error: string | undefined
  loading: boolean
}

interface Props {
  state: LatestState
  pollIntervalS: number
  /** The health monitor's expected interval and silent threshold for the
   *  station (the station summary), which judge each tile's freshness. */
  expectedS?: number
  silentAfterS?: number
  /** Parameters silent between events by design, given no verdict. */
  eventDriven?: Set<string>
}

const FRESH_WORD: Record<Freshness, string> = {
  ok: 'On time', late: 'Late', over: 'Overdue', never: 'No reading',
}

export function LatestReadings({ state, pollIntervalS, expectedS, silentAfterS,
                                 eventDriven }: Props) {
  const readings = orderReadings(state.data ?? [])
  const now = new Date()

  // The newest reading anywhere at the station. It is a max over every
  // channel, so it is set by whichever reports fastest — usually a CSQ, SNR
  // or battery diagnostic, not a measurement. At Bodorna a five-minute health
  // channel would hold this line at "reporting" while every piezometer was
  // dead, which is the blind spot station_day_completeness and
  // station_health.last_measurement_at already have on record.
  //
  // The line below therefore says what it is a fact about — the newest
  // channel — rather than speaking for the station. Naming the subject is the
  // fix available here; what "the station is reporting" should mean when some
  // channels have stopped is the same open question as the completeness one,
  // and belongs to the team. Each tile's own age and freshness word are the
  // channel-level truth.
  const newest = readings.reduce<string | null>((acc, r) => {
    if (!r.ts) return acc
    return !acc || new Date(r.ts).getTime() > new Date(acc).getTime() ? r.ts : acc
  }, null)

  const notice = noticeFor(readings.length, state.error, state.loading)

  return (
    <section className="live" aria-labelledby="latest-readings">
      <header>
        <h2 id="latest-readings">Latest Readings</h2>
        <p className="muted">
          Refreshes every 30 s.{' '}
          {newest
            ? `Newest channel: ${stalenessLabel(newest, pollIntervalS, now, silentAfterS)}`
              + ` · as of ${formatDateTime(newest)} · ${relativeAge(newest)}`
            // Silent unless a successful response proved there is nothing:
            // "no readings" off the back of a failed request is a claim about
            // the archive made on no evidence.
            : notice === 'empty' ? stalenessLabel(null, pollIntervalS) : ''}
        </p>
      </header>

      {notice === 'stale' ? (
        <p className="muted">
          Could not refresh ({state.error}). Showing the last readings received.
        </p>
      ) : null}

      {notice === 'failed' ? (
        <p className="muted">
          Could not load the latest readings ({state.error}).
        </p>
      ) : null}

      {notice === 'empty' ? (
        <p className="muted">No readings stored for this station yet.</p>
      ) : null}

      <ul className="reading-tiles">
        {readings.map((r) => {
          // Judged by the monitor's numbers only; without them a tile says
          // its age and no verdict.
          const fresh = verdict(r.ts, expectedS, silentAfterS, now,
                                eventDriven?.has(r.parameter_code) ?? false)
          const flags = isSuspect(r.quality) ? names(r.quality).join(', ') : ''
          return (
            <li key={r.parameter_code}
                className={`reading-tile${fresh ? ` fresh-${fresh}` : ''}`}
                title={flags ? `Quality: ${flags}` : undefined}>
              <span className="reading-tile-head">
                <span className="reading-tile-name">{r.display_name}</span>
                {fresh && <span className={`fresh-word word-${fresh}`}>{FRESH_WORD[fresh]}</span>}
              </span>
              {/* The stored value at the channel's recorded precision, the
                  exact reading in the title: a readout that replaces walking
                  to the sensor must not round three centimetres away. A
                  parameter with no unit carries "-", which beside a number
                  reads as a minus sign, so it is left out. */}
              <span className="reading-tile-value">
                <span className="mono" title={r.value === null ? undefined : String(r.value)}>
                  {displayValue(r.value, r.precision)}
                </span>
                {r.unit && r.unit !== '-' && <span className="reading-tile-unit">{r.unit}</span>}
              </span>
              <span className="reading-tile-age">{relativeAge(r.ts, now)}</span>
              {flags && <span className="reading-tile-flags">{flags}</span>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
